import React, { useCallback, useEffect, useState } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MdErrorOutline } from 'react-icons/md';
import {
  Button,
  PromoBanner,
  Spinner,
  TagPill,
  PROMO_BANNER_DENSITY_COMPACT,
  PROMO_BANNER_TONE_ERROR,
  PROMO_BANNER_TONE_INFO,
} from '@aibuild/ui';
import type { AppRunsOn } from '@api/apps';
import { getPublicApp, getPublicAppPage } from '@api/publicApps';
import { authPathWithReturnTo } from '@utils/ssoSession';
import AppFrame from '../appFrame/AppFrame';
import type { AppFramePage } from '../appFrame/appFramePage';
import { sharedAppPath } from '../appRunPath';
import { localized } from '../appRunView';
import { usesAgent } from '../appCapabilities';
import { PageBody, PageRoot } from '../AppRunPage.styled';
import AppWindow, { WindowSurface } from '../appFrame/AppWindow';
import AppLogo from '../run/AppLogo';
import { IdentityTags, PageTitle, TitleGroup } from '../run/AppRunHeader.styled';
import { useGuestAppController } from './useGuestAppController';
import SignupGateDialog, { type SignupReason } from './SignupGateDialog';
import { Byline, HeaderActions, PublicShell } from './PublicAppPage.styled';

const publicPage = (token: string, key: string): AppFramePage => ({ key, load: () => getPublicAppPage(token) });

const runsOnNote = (intl: IntlShape, runsOn: AppRunsOn | undefined): string | null => {
  if (runsOn === 'LOCAL') {
    return intl.formatMessage({
      id: 'apps.public.runsOn.local',
      defaultMessage: 'This app runs on your own computer once you have an account and a local runner.',
    });
  }
  if (runsOn === 'CHOICE') {
    return intl.formatMessage({
      id: 'apps.public.runsOn.choice',
      defaultMessage: 'Once you have an account, this app runs in the cloud or on your own computer.',
    });
  }
  return null;
};

/** A shared link is unlisted: search engines should not index it. */
const useNoIndex = () => {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
};

/**
 * An app opened through its owner's public link by someone with no account.
 * The page works as it would for a member; anything that would reach the
 * server opens the signup gate instead.
 */
const PublicAppPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { token = '' } = useParams<{ token: string }>();
  const [gate, setGate] = useState<SignupReason | null>(null);
  useNoIndex();
  const view = useQuery({
    queryKey: ['apps', 'public', token],
    queryFn: () => getPublicApp(token),
    retry: false,
  });
  const openGate = useCallback((reason: SignupReason) => setGate(reason), []);
  const controller = useGuestAppController(view.data?.manifest ?? null, openGate);
  const returnTo = sharedAppPath(token);

  let body: React.ReactNode;
  if (view.isLoading) {
    body = <Spinner label={intl.formatMessage({ id: 'apps.public.loading', defaultMessage: 'Opening this app…' })} />;
  } else if (!view.data) {
    body = (
      <PromoBanner
        tone={PROMO_BANNER_TONE_ERROR}
        icon={<MdErrorOutline />}
        title={intl.formatMessage({
          id: 'apps.public.unavailable',
          defaultMessage: 'This app is not available. The link may have been turned off.',
        })}
        dataTestId="public-app__unavailable"
      />
    );
  } else {
    const { manifest, creatorName, htmlPolicy } = view.data;
    const appName = localized(intl, manifest.name);
    const note = usesAgent(manifest) ? runsOnNote(intl, manifest.runsOn) : null;
    body = (
      <>
        {note && (
          <PromoBanner
            tone={PROMO_BANNER_TONE_INFO}
            density={PROMO_BANNER_DENSITY_COMPACT}
            title={note}
            dataTestId="public-app__runs-on"
          />
        )}
        <AppWindow
          bar={
            <>
              <TitleGroup>
                <AppLogo manifest={manifest} dataTestId="public-app__logo" />
                <PageTitle data-testid="public-app__title">{appName}</PageTitle>
              </TitleGroup>
              <Byline data-testid="public-app__creator">
                {creatorName
                  ? intl.formatMessage({ id: 'apps.public.byCreator', defaultMessage: 'by {creator}' }, { creator: creatorName })
                  : localized(intl, manifest.tagline)}
              </Byline>
              <IdentityTags>
                <TagPill
                  label={intl.formatMessage({ id: 'apps.public.madeByUser', defaultMessage: 'Made by an Aibuild user' })}
                  prefix={false}
                  size="sm"
                  dataTestId="public-app__made-by-user"
                />
              </IdentityTags>
            </>
          }
        >
          <WindowSurface>
            <AppFrame
              manifest={manifest}
              page={publicPage(token, manifest.presentation?.page ?? `${manifest.key}:${manifest.version}`)}
              controller={controller}
              title={appName}
              policy={htmlPolicy}
              untrusted
            />
          </WindowSurface>
        </AppWindow>
        <SignupGateDialog
          open={gate !== null}
          onClose={() => setGate(null)}
          appName={appName}
          returnTo={returnTo}
          reason={gate ?? undefined}
        />
      </>
    );
  }

  return (
    <PublicShell>
      <PageRoot data-testid="public-app">
        <PageBody>
          <HeaderActions>
            <Button variant="text" size="sm" onClick={() => navigate(authPathWithReturnTo('login', returnTo))} dataTestId="public-app__login">
              {intl.formatMessage({ id: 'apps.public.login', defaultMessage: 'Log in' })}
            </Button>
            <Button size="sm" onClick={() => navigate(authPathWithReturnTo('signup', returnTo))} dataTestId="public-app__signup">
              {intl.formatMessage({ id: 'apps.public.signup', defaultMessage: 'Sign up' })}
            </Button>
          </HeaderActions>
          {body}
        </PageBody>
      </PageRoot>
    </PublicShell>
  );
};

export default PublicAppPage;
